// src/hooks/useMessengerQueries.ts (수정된 버전)
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import useAuthEP from '../utils/useAuthEP';
import {
  getMyChannels,
  getPublicChannels,
  searchChannels,
  getChannel,
  createChannel,
  updateChannel,
  deleteChannel,
  joinChannel,
  leaveChannel,
  getChannelMessages,
  sendMessage,
  updateMessage,
  deleteMessage,
  addReaction,
  pinMessage,
  unpinMessage,
  markAsRead,
  markChannelAsRead,
  getChannelMembers,
  getOnlineMembers,
  addMember,
  removeMember,
  updateMyNickname,
  updateNotificationSettings,
  muteMember,
  unmuteMember,
  banMember,
  unbanMember,
  uploadFile,
  uploadMultipleFiles, getReplies, getMentionedMessages, updateMemberRole,
} from '../endpoints/messenger-endpoints';
import {
  Channel, ChannelApiResponse,
  ChannelListApiResponse, ChannelListResponse,
  ChannelMember, ChannelMemberListApiResponse,
  Message,
  MessageListApiResponse, MessageListResponse
} from "../types/messenger/messengerTypes";

// ===== 일반 쿼리들 (타입 명시) =====

// 내 채널 목록 조회
export const useMyChannels = (params?: { size?: number; sortBy?: string; sortDirection?: string }) => {
  const authEP = useAuthEP();
  
  return useQuery<ChannelListApiResponse['data']>({
    queryKey: ['channels', 'my', params],
    queryFn: async () => {
      const response: ChannelListApiResponse = await authEP({
        func: getMyChannels,
        params
      });
      console.log("getMyChannels, ", response.data)
      return response.data;
    },
    staleTime: 1000 * 60 * 1, // 1분
    refetchOnMount: 'always', // 컴포넌트 마운트 시마다 새로고침
    
  });
};

// 공개 채널 목록 조회
export const usePublicChannels = (params?: { size?: number; sortBy?: string; sortDirection?: string }) => {
  const authEP = useAuthEP();
  
  return useQuery<ChannelListApiResponse['data']>({
    queryKey: ['channels', 'public', params],
    queryFn: async () => {
      const response: ChannelListApiResponse = await authEP({
        func: getPublicChannels,
        params
      });
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// 채널 검색
export const useSearchChannels = (keyword: string, params?: { size?: number; cursor?: string }) => {
  const authEP = useAuthEP();
  
  return useQuery<ChannelListApiResponse['data']>({
    queryKey: ['channels', 'search', keyword, params],
    queryFn: async () => {
      const response: ChannelListApiResponse = await authEP({
        func: searchChannels,
        params: { keyword, ...params }
      });
      return response.data;
    },
    enabled: keyword.length > 2,
  });
};

// 채널 상세 조회
export const useChannel = (channelId: string | null) => {
  const authEP = useAuthEP();
  
  return useQuery<Channel>({
    queryKey: ['channels', channelId],
    queryFn: async () => {
      const response: ChannelApiResponse = await authEP({
        func: getChannel,
        params: { channelId: channelId! }
      });
      return response.data;
    },
    enabled: !!channelId,
  });
};

// 채널 멤버 목록 조회
export const useChannelMembers = (channelId: string | null) => {
  const authEP = useAuthEP();
  
  return useQuery<ChannelMember[]>({
    queryKey: ['members', channelId],
    queryFn: async () => {
      const response: ChannelMemberListApiResponse = await authEP({
        func: getChannelMembers,
        params: { channelId: channelId! }
      });
      return response.data;
    },
    enabled: !!channelId,
    staleTime: 1000 * 10,
  });
};

// 온라인 멤버 조회
export const useOnlineMembers = (channelId: string | null) => {
  const authEP = useAuthEP();
  
  return useQuery<ChannelMember[]>({
    queryKey: ['members', 'online', channelId],
    queryFn: async () => {
      const response: ChannelMemberListApiResponse = await authEP({
        func: getOnlineMembers,
        params: { channelId: channelId! }
      });
      return response.data;
    },
    enabled: !!channelId,
    refetchInterval: 1000 * 10, // 10초마다 새로고침
  });
};

// 멤버 권한 변경
export const useUpdateMemberRole = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({channelId, userId, roleId}: {
      channelId : string;
      userId : string;
      roleId : string;
    }) => authEP({
      func: updateMemberRole,
      params : { channelId, userId, roleId }
    }),
    onSuccess: (_, variables) => {
      
      // 멤버 목록 새로고침
      queryClient.invalidateQueries(({
        queryKey: ["members", variables.channelId]
      }));
      
      // 채널 목록 새로고침
      queryClient.invalidateQueries(({
        queryKey: ['channels', 'my']
      }));
      
      // 온라인 멤버 새로고침
      queryClient.invalidateQueries(({
        queryKey: ['members', 'online', variables.channelId]
      }));
    }
  })
}

// 채널 생성 뮤테이션
export const useCreateChannel = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      channelType: Channel['channelType'];
      password?: string;
      requiresApproval?: boolean;
      maxMembers?: number;
      topic?: string;
      tags?: string[];
      initialMemberIds?: string[];
    }) => authEP({ func: createChannel, params: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'my'] });
    },
  });
};

// 채널 수정 뮤테이션
export const useUpdateChannel = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ channelId, data }: { channelId: string; data: any }) =>
      authEP({ func: updateChannel, params: { channelId, data } }),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(['channels', variables.channelId], response);
      queryClient.invalidateQueries({ queryKey: ['channels', 'my'] });
    },
  });
};

// 채널 삭제 뮤테이션
export const useDeleteChannel = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (channelId: string) =>
      authEP({ func: deleteChannel, params: { channelId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'my'] });
    },
  });
};

// 채널 참여 뮤테이션
export const useJoinChannel = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ channelId, data }: { channelId: string; data?: any }) =>
      authEP({ func: joinChannel, params: { channelId, data } }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'my'] });
      queryClient.setQueryData(['channels', response.data.id], response);
    },
  });
};

// 채널 탈퇴 뮤테이션
export const useLeaveChannel = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (channelId: string) =>
      authEP({ func: leaveChannel, params: { channelId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels', 'my'] });
    },
  });
};

// ===== 메시지 관련 훅 =====

// useMessengerQueries.ts
export const useChannelMessages = (channelId: string | null) => {
  const authEP = useAuthEP();
  return useInfiniteQuery<MessageListResponse>({
    queryKey: ['messages', channelId],
    queryFn: async ({pageParam}) => {
      const response = await authEP({
        func : getChannelMessages,
        params: {
          channelId : channelId!,
          size: 20,
          cursor:pageParam ?? undefined
        }
      })
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.cursor : undefined;
    },
    enabled: !!channelId,
    staleTime: 0,           // ✅ 항상 stale 상태로 간주
    gcTime: 0,              // ✅ 캐시 즉시 삭제 (채널 변경 시 이전 데이터 제거)
    refetchOnMount: true,   // ✅ 마운트 시 항상 refetch
  });
};

// 답글 조회 (무한 스크롤)
export const useReplies = (parentMessageId: string | null) => {
  const authEP = useAuthEP();
  
  return useInfiniteQuery({
    queryKey: ['replies', parentMessageId],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const response: MessageListApiResponse = await authEP({
        func: getReplies,
        params: { parentMessageId: parentMessageId!, cursor: pageParam, size: 20 }
      });
      return response.data;
    },
    enabled: !!parentMessageId,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.cursor : undefined;
    },
  });
};

// 멘션된 메시지 조회 (무한 스크롤)
export const useMentionedMessages = () => {
  const authEP = useAuthEP();
  
  return useInfiniteQuery({
    queryKey: ['mentions'],
    queryFn: async ({ pageParam }: { pageParam: string | null }) => {
      const response: MessageListApiResponse = await authEP({
        func: getMentionedMessages,
        params: { cursor: pageParam, size: 20 }
      });
      return response.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.cursor : undefined;
    },
  });
};

// 메시지 전송 뮤테이션
// src/hooks/useMessengerQueries.ts
export const useSendMessage = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: {
      channelId: string;
      message: string;
      messageType: Message['messageType'];
      parentMessageId?: string;
      attachments?: any[];
      mentionedUserIds?: string[];
      clientId?: string;
    }) => authEP({ func: sendMessage, params: data }),
    onSuccess: (response, variables) => {
      
      // ✅ isMyMessage 추가
      response.data.isMyMessage = true;
      
      // ✅ 메시지 목록에 새 메시지 추가
      queryClient.setQueryData(['messages', variables.channelId], (old: any) => {
        if (!old?.pages) return old;
        
        const newPages = [...old.pages];
        
        // 첫 번째 페이지(최신 메시지들)에 새 메시지 추가
        if (newPages.length > 0) {
          newPages[0] = {
            ...newPages[0],
            messages: [...newPages[0].messages, response.data] // ✅ 단순히 추가
          };
        }
        console.log("old", old)
        console.log("newPages", newPages)
        return { ...old, pages: newPages };
      });
      
      // ✅ 채널 목록 업데이트
      queryClient.setQueryData(['channels', 'my'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          channels: old.channels.map((channel: Channel) =>
            channel.id === variables.channelId
              ? {
                ...channel,
                lastMessage: response.data,
                lastMessageAt: response.data.createdAt
              }
              : channel
          )
        };
      });
    },
  });
};

// 메시지 수정 뮤테이션
export const useUpdateMessage = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ messageId, data }: { messageId: string; data: { message: string } }) =>
      authEP({ func: updateMessage, params: { messageId, data } }),
    onSuccess: (response, variables) => {
      // 메시지 목록에서 해당 메시지 업데이트
      queryClient.setQueryData(['messages'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.map((msg: Message) =>
              msg.id === variables.messageId ? response.data : msg
            ),
          })),
        };
      });
    },
  });
};

// 메시지 삭제 뮤테이션
export const useDeleteMessage = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (messageId: string) =>
      authEP({ func: deleteMessage, params: { messageId } }),
    onSuccess: (_, messageId) => {
      // 메시지 목록에서 해당 메시지 제거
      queryClient.setQueryData(['messages'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.filter((msg: Message) => msg.id !== messageId),
          })),
        };
      });
    },
  });
};

// 메시지 반응 뮤테이션
export const useAddReaction = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ messageId, data }: { messageId: string; data: { emoji: string; isAdd: boolean } }) =>
      authEP({ func: addReaction, params: { messageId, data } }),
    onSuccess: (response, variables) => {
      // 메시지 반응 업데이트
      queryClient.setQueryData(['messages'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.map((msg: Message) =>
              msg.id === variables.messageId
                ? response.data
                : msg
            ),
          })),
        };
      });
    },
  });
};

// 메시지 고정/해제 뮤테이션
export const usePinMessage = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ messageId, pin }: { messageId: string; pin: boolean }) =>
      pin
        ? authEP({ func: pinMessage, params: { messageId } })
        : authEP({ func: unpinMessage, params: { messageId } }),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(['messages'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            messages: page.messages.map((msg: Message) =>
              msg.id === variables.messageId
                ? response.data
                : msg
            ),
          })),
        };
      });
    },
  });
};

// 읽음 처리 뮤테이션
export const useMarkAsRead = () => {
  const authEP = useAuthEP();
  
  return useMutation({
    mutationFn: ({ channelId, messageId }: { channelId: string; messageId?: string }) =>
      messageId
        ? authEP({ func: markAsRead, params: { messageId, channelId } })
        : authEP({ func: markChannelAsRead, params: { channelId } }),
  });
};

// ===== 채널 멤버 관련 훅 =====

// 멤버 추가 뮤테이션
export const useAddMember = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ channelId, userId }: { channelId: string; userId: string }) =>
      authEP({ func: addMember, params: { channelId, userId } }),
    onSuccess: (_, variables) => {
      // 멤버 목록 새로고침
      queryClient.invalidateQueries({ queryKey: ['members', variables.channelId] });
      // ✅ 내 채널 목록도 새로고침 (memberCount 업데이트)
      queryClient.invalidateQueries({ queryKey: ['channels', 'my'] });
      // ✅ 해당 채널 상세 정보도 새로고침
      queryClient.invalidateQueries({ queryKey: ['channels', variables.channelId] });
    },
  });
};

// 멤버 제거 뮤테이션
export const useRemoveMember = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ channelId, userId }: { channelId: string; userId: string }) =>
      authEP({ func: removeMember, params: { channelId, userId } }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['members', variables.channelId] });
    },
  });
};

// 내 닉네임 변경 뮤테이션
export const useUpdateMyNickname = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ channelId, nickname }: { channelId: string; nickname: string }) =>
      authEP({ func: updateMyNickname, params: { channelId, nickname } }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['members', variables.channelId] });
    },
  });
};

// 알림 설정 변경 뮤테이션
export const useUpdateNotificationSettings = () => {
  const authEP = useAuthEP();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ channelId, level, enabled }: {
      channelId: string;
      level: ChannelMember['notificationLevel'];
      enabled: boolean
    }) =>
      authEP({ func: updateNotificationSettings, params: { channelId, level, enabled } }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['members', variables.channelId] });
    },
  });
};

// 파일 업로드 뮤테이션
export const useFileUpload = () => {
  const authEP = useAuthEP();
  
  return useMutation({
    mutationFn: (file: File) =>
      authEP({ func: uploadFile, params: { file } }),
    onSuccess: (response) => {
      console.log('File uploaded:', response.data);
    },
  });
};

// 여러 파일 업로드 뮤테이션
export const useMultipleFileUpload = () => {
  const authEP = useAuthEP();
  
  return useMutation({
    mutationFn: (files: File[]) =>
      authEP({ func: uploadMultipleFiles, params: { files } }),
    onSuccess: (response) => {
      console.log('Files uploaded:', response.data);
    },
  });
};